import { hightlightColors } from '../../../constants/highlights-colors';
import { hightlightColorsAlpha } from '../../../constants/highlights-colors-alpha';
import {
  HighlightColorsEnum,
  HighlightUnderlineEnum,
  HtmlElAltIdEnum,
} from '../../../enums';
import TrashIcon from '../../../assets/icons/trash.svg?react';
import WaterDropIcon from '../../../assets/icons/water-drop.svg?react';
import { HighlightOperationEnum, HighlightTooltipProps } from './types';
import { createPortal } from 'react-dom';
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../../utils/common-utils';
import { useHighlight } from '../../../contexts/highlight-provider/useHighlight';
import SticketSvg from '../../../assets/icons/sticker-svg';

const UnderlineDropdownArrow = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 8 12"
    aria-hidden="true"
    className={cn(
      'h-[12px] w-[8px] transition-transform',
      open ? '-rotate-90' : 'rotate-90',
    )}
    fill="none"
  >
    <path
      d="M1.5 1L6.5 6L1.5 11"
      stroke="#000000"
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HighlightTooltip = ({
  onMutateHighlight,
  highlightOperation,
}: HighlightTooltipProps): JSX.Element | null => {
  const {
    lastUsedColor,
    onRemoveHighlight,
    selectedHighlightKeyId,
    tooltipPos,
    selectedHighLight,
  } = useHighlight();

  const [underlineToggle, setUnderlineToggle] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const highlightTooltipRoot = document.getElementById(
    'highlight-tooltip-root',
  );

  const hightlightUnderlines: Record<HighlightUnderlineEnum, string> = {
    [HighlightUnderlineEnum.SOLID]: '_______',
    [HighlightUnderlineEnum.DASHED]: '_ _ _ _ _',
    [HighlightUnderlineEnum.DOTTED]: '. . . . . .',
  };

  useLayoutEffect(() => {
    if (!tooltipPos) return;

    const tooltipEl = tooltipRef.current;

    if (!tooltipEl) return;

    const viewportMargin = 12;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const viewportRight = window.innerWidth - viewportMargin;

    let nextOffset = 0;

    if (tooltipRect.left < viewportMargin) {
      nextOffset = viewportMargin - tooltipRect.left;
    } else if (tooltipRect.right > viewportRight) {
      nextOffset = viewportRight - tooltipRect.right;
    }

    setHorizontalOffset((currentOffset) =>
      currentOffset === nextOffset ? currentOffset : nextOffset,
    );
  }, [tooltipPos, highlightOperation, underlineToggle]);

  if (!highlightTooltipRoot || !tooltipPos) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      data-alt-type={HtmlElAltIdEnum.HIGHLIGHT_TOOLTIP}
      className="absolute z-[60] flex items-center gap-1 rounded-full border bg-white px-1.5 py-[0.25rem] text-sm shadow-md"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        top: tooltipPos.top,
        left: tooltipPos.left,
        transform: `translateX(calc(-50% + ${horizontalOffset}px))`,
      }}
    >
      <div className={'flex items-center gap-2'}>
        {Object.values(HighlightColorsEnum).map((color) => {
          const isSameColor = selectedHighLight?.color === color;

          return (
            <button
              className="group relative h-10 w-10 overflow-hidden rounded-full"
              onClick={() => {
                if (!isSameColor) onMutateHighlight({ color });
              }}
            >
              <div
                className={cn(
                  'absolute inset-0 rounded-full border border-black transition-colors',
                  !isSameColor && 'group-hover:opacity-0',
                )}
                style={{
                  backgroundColor: hightlightColorsAlpha[color],
                }}
              />

              <div
                className={cn(
                  'absolute inset-0 rounded-full border-2 border-black border-opacity-75 transition-colors',
                  !isSameColor &&
                    'opacity-0 hover:border-2 group-hover:opacity-100',
                )}
                style={{
                  backgroundColor: hightlightColors[color],
                }}
              />

              {isSameColor && (
                <div className={'position-center'}>
                  <WaterDropIcon
                    width={24}
                    height={24}
                    fill={hightlightColors[color]}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative mx-1 mt-[-0.25rem] flex items-center">
        <button
          type="button"
          className="group relative flex select-none flex-col items-center pr-3"
          onClick={() => setUnderlineToggle((prevState) => !prevState)}
        >
          <span className={'flex flex-col text-center font-inter text-xl'}>
            U
          </span>

          <div
            className={
              'align-middlew mt-[-0.25rem] flex w-full max-w-[2rem] flex-col justify-center overflow-hidden text-center'
            }
          >
            <span className={'mt-[-0.625rem] text-nowrap text-xs'}>
              _______
            </span>
            <span className={'mt-[-0.625rem] text-nowrap text-xs'}>
              _ _ _ _ _
            </span>
            <span className={'mt-[-0.5rem] text-nowrap text-xs'}>
              . . . . . .
            </span>
          </div>

          <div
            className="absolute right-[1px] top-[1.45rem]"
          >
            <UnderlineDropdownArrow open={underlineToggle} />
          </div>

          <div
            className={cn(
              'absolute -bottom-1 left-[-2px] right-[4px] h-[1.5px] bg-black transition-opacity',
              underlineToggle ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          />
        </button>

        {underlineToggle && (
          <div
            className={
              'absolute bottom-0 left-1/2 flex -translate-x-1/2 translate-y-full flex-col rounded-b-xl border border-t-0 bg-white pb-2'
            }
          >
            {Object.values(HighlightUnderlineEnum).map((underLine) => {
              const isSameUnderline =
                underLine === selectedHighLight?.underLine;

              return (
                <div
                  key={underLine}
                  className={
                    'group relative flex select-none flex-col items-center px-2 py-1 transition-colors duration-300 ease-in-out hover:bg-light-gray'
                  }
                  onClick={() => {
                    if (isSameUnderline) return;
                    setUnderlineToggle(false);
                    onMutateHighlight({ underLine });
                  }}
                >
                  <span className={'flex font-inter text-xl'}>U</span>

                  <div
                    className={
                      'align-middlew flex w-full max-w-[2rem] justify-center overflow-hidden'
                    }
                  >
                    <span className={'mt-[-0.625rem] text-nowrap text-xs'}>
                      {hightlightUnderlines[underLine]}
                    </span>
                  </div>

                  <div
                    className={cn(
                      'absolute bottom-0 left-0 h-full w-1 bg-gray',
                      !isSameUnderline &&
                        'bg-black opacity-0 transition-opacity group-hover:opacity-100',
                    )}
                  />
                </div>
              );
            })}

            <div
              className={
                'group relative flex select-none items-center px-2 py-1 transition-colors duration-300 ease-in-out hover:bg-light-gray'
              }
              key={'underLine-none'}
              onClick={() => {
                setUnderlineToggle(false);
                onMutateHighlight({ underLine: null! });
              }}
            >
              <div
                className={
                  'align-middlew flex w-full max-w-[2rem] justify-center'
                }
              >
                <span className={'text-sm'}>None</span>
              </div>

              <div
                className={
                  'absolute bottom-0 left-0 h-full w-1 bg-black opacity-0 transition-opacity group-hover:opacity-100'
                }
              />
            </div>
          </div>
        )}
      </div>

      <div className={'ml-1 flex items-center gap-1'}>
        {highlightOperation === HighlightOperationEnum.UPDATE && (
          <button
            className={
              'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-black border-opacity-75 hover:border-2'
            }
            onClick={() => {
              onRemoveHighlight(selectedHighlightKeyId);
            }}
          >
            <TrashIcon width={28} height={28} />
          </button>
        )}

        <div className="mx-1 h-8 w-px bg-[#D1D5DB]" />

        <button
          onClick={() => {
            onMutateHighlight({
              sticker: { isNeedSticker: true },
            });
          }}
          className={
            'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-black border-opacity-75 hover:border-2'
          }
        >
          <SticketSvg
            bgColor={
              hightlightColorsAlpha[selectedHighLight?.color || lastUsedColor]
            }
          />
        </button>
      </div>
    </div>,
    highlightTooltipRoot,
  );
};

export default HighlightTooltip;
